import { computed, ref } from 'vue';
import { toSafeInteger } from 'lodash-es';

export const TOURS_DEFAULT_CASHBACK_PERCENT = 5;
export const TOURS_MAX_CASHBACK = 100_000;

export const MAX_CASHBACK_PERCENT = 10;

const REGRESS_CASHBACK = {
  default: {
    transactions: [5000, 7000],
    percent: 0.5,
  },
  increased: {
    transactions: [20_000, 30_000],
    percent: 1,
  },
};

const REGRESS_CASHBACK_PERCENTS = [
  REGRESS_CASHBACK.default.percent,
  REGRESS_CASHBACK.increased.percent,
];

const getRegressCashback = (
  limitPerTransaction: number,
  offerPrice: number,
) => {
  const target = Object.values(REGRESS_CASHBACK).filter(({ transactions }) =>
    transactions.includes(limitPerTransaction),
  )[0];

  if (!target) {
    return;
  }

  return Math.min(
    Math.round((offerPrice / 100) * target.percent),
    limitPerTransaction,
  );
};

/**
 * @description Так как у кешбэков для туров другие правила, пришлось написать отдельную модельку для работы с ним
 */
export const useFreedomCashback = (initialPrice?: string) => {
  const offerPrice = ref(initialPrice);
  //  TODO: Uncomment when authorization logic is implemented
  // const freedomCashbackQuery = useFreedomCashbackQuery();

  const isLoading = computed(() => false);
  const limitPerMonth = computed(() => toSafeInteger(200_000));
  const limitPerTransaction = computed(() => toSafeInteger(30_000));

  const cashbackPercent = computed(() => {
    const totalPercent = toSafeInteger(MAX_CASHBACK_PERCENT);

    /**
     * @description Наш API хардкодит 0.5 или 1 процент, если клиент ушел в регресс (пробил все лимиты).
     * Это делается, чтобы в Авиа и ЖД правильно считался кешбэк. В турах он считается по-другому,
     * поэтому, чтобы отображать правильные процент кешбэка, мы к полученному проценту добавляем туровский
     */
    if (REGRESS_CASHBACK_PERCENTS.includes(totalPercent)) {
      return totalPercent + TOURS_DEFAULT_CASHBACK_PERCENT;
    }

    return totalPercent;
  });

  /**
   * @description На стороне фридома сделали минимальную доработку - увеличили процент кешбэка,
   * но при этом, лимиты остались такими же, а туры всегда за них будут выходить.
   * Поэтому, мы минусуем то, что туры добавляют сверху, чтобы высчитать правильных транзакционный кешбэк
   */
  const cashbackPercentWithoutTours = computed(
    () => toSafeInteger(MAX_CASHBACK_PERCENT) - TOURS_DEFAULT_CASHBACK_PERCENT,
  );

  // Туровой кешбэк - всегда `TOURS_DEFAULT_CASHBACK_PERCENT`% от стоимости тура или `TOURS_MAX_CASHBACK`
  const toursCashbackAmount = computed(() =>
    Math.min(
      Math.round(
        (toSafeInteger(offerPrice.value) / 100) *
          TOURS_DEFAULT_CASHBACK_PERCENT,
      ),
      TOURS_MAX_CASHBACK,
    ),
  );

  const amount = computed(() => {
    const receivedCashBackWithTours =
      toSafeInteger(0) + toursCashbackAmount.value;

    const isClientInRegress = REGRESS_CASHBACK_PERCENTS.includes(
      toSafeInteger(MAX_CASHBACK_PERCENT),
    );

    /**
     * @description Так как туровой кешбэк съедает остаток ежемесячного, если он больше ежемесячного,
     * то мы возвращаем туровой, без добавления транзакционного,
     * но прибавляем к нему регрессионный кешбэк.
     * Регрессионный кешбэк - это кешбэк, который пользователь получает, когда у него пробиты все лимиты
     *
     * @note Наш API хардкодит 0.5 или 1 процент, если клиент ушел в регресс (пробил все лимиты) и сбрасывает
     * `received_cashback_sum` до 0, чтобы в Авиа и ЖД кешбэк считался нормально.
     * Из-за этих изменений сравнение полученного кешбэка и лимитов ломается и кешбэк считается неправильно,
     * поэтому мы просто смотрим если полученный процент кешбэка равен одному из процентов регрессионого кешбэка
     */
    if (isClientInRegress || receivedCashBackWithTours >= limitPerMonth.value) {
      const regressCashback = getRegressCashback(
        toSafeInteger(30_000),
        toSafeInteger(offerPrice.value),
      );

      return regressCashback
        ? toursCashbackAmount.value + regressCashback
        : toursCashbackAmount.value;
    }

    const transactionCashback = Math.round(
      (toSafeInteger(offerPrice.value) / 100) *
        cashbackPercentWithoutTours.value,
    );

    const effectiveCashback = Math.min(
      transactionCashback,
      toSafeInteger(30_000),
    );

    // Высчитываем разницу остатка ежемесячного кешбэка.
    // Это нужно, чтобы понять сколько нужно добавить к статичному туровому кешбэку
    const limitDifference = limitPerMonth.value - receivedCashBackWithTours;

    if (limitDifference >= effectiveCashback) {
      return effectiveCashback + toursCashbackAmount.value;
    }

    return limitDifference + toursCashbackAmount.value;
  });

  const baseCashbackPercent = computed(
    () => cashbackPercent.value - TOURS_DEFAULT_CASHBACK_PERCENT,
  );
  const baseCashbackSum = computed(
    () => amount.value - toursCashbackAmount.value,
  );

  const changeOfferPrice = (newPrice: string) => {
    offerPrice.value = newPrice;
  };

  return {
    isLoading,
    amount,
    toursCashbackAmount,
    cashbackPercent,
    limitPerMonth,
    limitPerTransaction,
    changeOfferPrice,
    baseCashbackPercent,
    baseCashbackSum,
  };
};
